import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getRegions } from '@luxuryescapes/lib-regions';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { ensureAgentHubBrands } from '~/consts/agentHub';

import useCurrentBrand from '~/hooks/useCurrentBrand';
import useQuery from '~/hooks/useQuery';

import { listAffiliations } from '~/services/AgentHub/AgentService';

import { EoiRegionCode } from '~/types/services/agentHub';

import AddAffiliationModal from './AddAffiliationModal';
import AffiliationEditorModal from './AffiliationEditorModal';
import DeleteAffiliationModal, { DeleteAffiliationModalData } from './DeleteAffiliationModal';

const DEFAULT_SIZE_PER_PAGE = 10;

export default function AffiliationList() {
  const { push: setQueryString } = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const queryFilters = useQuery();

  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [affiliationList, setAffiliationList] = useState([]);
  const [affiliationTotalCount, setAffiliationTotalCount] = useState(0);
  const [isAddAffiliationModalOpen, setIsAddAffiliationModalOpen] = useState(false);
  const brand = useCurrentBrand();
  const regions = useMemo(
    () => [
      { code: 'all', name: 'All' },
      { code: 'world', name: 'World' },
      ...getRegions(brand).map((region) => ({ code: region.code, name: region.name })),
    ],
    [brand],
  );

  const [editor, setEditor] = useState({ id: null, opened: false });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<DeleteAffiliationModalData>({
    id: null,
    name: '',
    open: false,
  });

  const filter = useMemo(() => {
    return {
      page: parseInt(queryFilters.get('page') ?? '0'),
      order: queryFilters.get('order'),
      status: queryFilters.get('status'),
      search: queryFilters.get('search'),
      region: queryFilters.get('region'),
    };
  }, [queryFilters]);

  const openAddAffiliationModal = () => {
    setIsAddAffiliationModalOpen(true);
  };

  const closeAddAffiliationModal = () => {
    setIsAddAffiliationModalOpen(false);
  };

  const onCloseDeleteModal = ({ refetch = true }) => {
    if (refetch) {
      fetchAgencyList();
    }

    setIsDeleteModalOpen({ id: null, name: '', open: false });
  };

  const onPageChange = useCallback(
    (page: number) => {
      queryFilters.set('page', page.toString());
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onSearchChange = useCallback(
    (search: string) => {
      if (search === '') {
        queryFilters.delete('search');
      } else {
        queryFilters.set('search', search);
      }
      queryFilters.set('page', '0');
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onRegionChange = useCallback(
    (region: string) => {
      if (region === 'all') {
        queryFilters.delete('region');
      } else {
        queryFilters.set('region', region);
      }
      queryFilters.set('page', '0');
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onSearchChangeDebounced = debounce(onSearchChange, 500);

  const fetchAgencyList = useCallback(async (): Promise<void> => {
    try {
      setLoadingState('loading');
      const { search, region } = filter;

      const { rows, total } = await listAffiliations({
        page: filter.page.toString(),
        pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
        ...(search ? { search } : {}),
        ...(region ? { region: region as EoiRegionCode } : {}),
        brand: ensureAgentHubBrands(brand),
      });

      setAffiliationTotalCount(total);
      setAffiliationList(rows);
      setLoadingState('success');
    } catch (e) {
      setLoadingState('failed');
      enqueueSnackbar(`Failed to load data: ${e}`, {
        variant: 'error',
      });
    }
  }, [filter, enqueueSnackbar]);

  const closeEditorModal = useCallback(() => {
    setEditor({ id: null, opened: false });
    fetchAgencyList();
  }, [setEditor, fetchAgencyList]);

  useEffect(() => {
    fetchAgencyList();
  }, [fetchAgencyList]);

  const columns: Array<GridColDef> = useMemo(() => {
    return [
      { field: 'name', headerName: 'Name', flex: 1, sortable: false, display: 'flex' },
      { field: 'region', headerName: 'Region', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'isManageable',
        headerName: 'Is managed affiliation',
        flex: 1,
        sortable: false,
        display: 'flex',
        renderCell: (params) => {
          return params.row.isManageable ? 'Yes' : 'No';
        },
      },
      {
        field: 'action',
        headerName: '',
        flex: 1,
        renderCell: (params) => {
          return (
            <>
              <Button variant="text" onClick={() => setEditor({ id: params.row.id, opened: true })}>
                Edit
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteModalOpen({ id: params.row.id, name: params.row.name, open: true });
                }}
              >
                Delete
              </Button>
            </>
          );
        },
        display: 'flex',
      },
    ];
  }, []);

  return (
    <>
      <Stack gap={2} display="flex" justifyContent="end">
        <Stack direction="row" gap={1}>
          <FormControl sx={{ display: 'flex', gap: 1, flex: 1 }}>
            <TextField
              id="search-input-label"
              data-testid="search"
              type="text"
              name="search"
              label="Search"
              onChange={(e) => onSearchChangeDebounced(e.target.value)}
            />
          </FormControl>
          <FormControl sx={{ display: 'flex', gap: 1 }}>
            <InputLabel id="region-input-label">Region</InputLabel>
            <Select
              value={filter.region ?? 'all'}
              name="region"
              label="Region"
              onChange={(e) => onRegionChange(e.target.value)}
            >
              {regions.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Button sx={{ flex: 1, alignSelf: 'end' }} variant="contained" onClick={openAddAffiliationModal}>
          Add Affiliation
        </Button>
      </Stack>
      <Stack mt={1}>
        <DataGrid
          rows={affiliationList}
          rowCount={affiliationTotalCount}
          columns={columns}
          loading={loadingState == 'loading'}
          pagination
          paginationMode="server"
          paginationModel={{ page: filter.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          slots={{ pagination: GridPagination }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
        />
        <AffiliationEditorModal onClose={closeEditorModal} id={editor.id} opened={editor.opened} />
        <AddAffiliationModal onClose={closeAddAffiliationModal} isOpen={isAddAffiliationModalOpen} />
        <DeleteAffiliationModal isOpen={isDeleteModalOpen.open} onClose={onCloseDeleteModal} data={isDeleteModalOpen} />
      </Stack>
    </>
  );
}
